import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet, Svg, Circle } from '@react-pdf/renderer';
import type { ScenarioWithImprovements } from '@/types/work-scenarios';
import { registerPDFFonts } from '@/utils/pdf-fonts';

registerPDFFonts();

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

interface ScenarioPDFDocumentProps {
  companyName: string;
  logoUrl: string | null;
  primaryColor?: string;
  scenario: ScenarioWithImprovements;
  dpeData: any;
}

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const getDPEColor = (cls: string): string => {
  const colors: Record<string, string> = {
    A: '#009966', B: '#33CC33', C: '#CCFF00',
    D: '#FFFF00', E: '#FF9900', F: '#FF6600', G: '#FF0000',
  };
  return colors[cls?.toUpperCase()] ?? '#9CA3AF';
};

const getDPETextColor = (cls: string): string =>
  ['C', 'D'].includes(cls?.toUpperCase()) ? '#1F2937' : '#FFFFFF';

const formatCurrency = (val: number): string => {
  if (val == null) return '—';
  const rounded = Math.round(val);
  return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' €';
};

const formatNumber = (val: number, decimals = 0): string => {
  if (val == null) return '—';
  const fixed = val.toFixed(decimals);
  const [int, dec] = fixed.split('.');
  const intFormatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return dec !== undefined ? intFormatted + ',' + dec : intFormatted;
};

const formatDateFR = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return dateStr; }
};

const truncate = (str: string | undefined | null, max: number): string => {
  if (!str) return '';
  return str.length > max ? str.slice(0, max - 1) + '...' : str;
};

const getROIColor = (years: number): string => {
  if (years < 8) return '#16A34A';
  if (years <= 15) return '#D97706';
  return '#DC2626';
};

const getImprovementLabel = (type: string): string => {
  const labels: Record<string, string> = {
    'heating': 'Chauffage',
    'wall_insulation': 'Isolation des murs',
    'insulation_walls': 'Isolation des murs',
    'roof_insulation': 'Isolation toiture',
    'insulation_roof': 'Isolation toiture',
    'floor_insulation': 'Isolation plancher bas',
    'insulation_floor': 'Isolation plancher bas',
    'windows': 'Menuiseries',
    'ventilation': 'Ventilation',
    'hot_water': 'Eau chaude sanitaire',
    'heating_control': 'Régulation chauffage',
    'solar': 'Énergie solaire',
    'other': 'Autre',
  };
  return labels[type] ?? type ?? 'Travaux';
};

const calculateROI = (imp: any): number | null => {
  if (imp.payback_period_years != null) return imp.payback_period_years;
  if (imp.estimated_cost && imp.cost_savings && imp.cost_savings > 0) {
    return imp.estimated_cost / imp.cost_savings;
  }
  return null;
};

// ─────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────

const s = StyleSheet.create({
  page: {
    paddingTop: 22,
    paddingBottom: 30,
    paddingHorizontal: 26,
    fontSize: 9,
    fontFamily: 'Roboto',
    backgroundColor: '#FFFFFF',
  },

  // ── Header branding ──
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: '#2563EB',
    marginBottom: 0,
  },
  headerLeft: { flexDirection: 'column' },
  companyName: { fontSize: 14, fontWeight: 'bold', color: '#1F2937' },
  headerTitle: { fontSize: 9, color: '#6B7280', marginTop: 1 },
  headerSubtitle: { fontSize: 8, color: '#9CA3AF', marginTop: 2 },
  logo: { width: 120, height: 56, objectFit: 'contain' },

  // ── Bandeau identité DPE ──
  dpeBandeau: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 5,
    padding: 8,
    marginTop: 7,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dpeMetaLeft: { flex: 1 },
  dpeAddress: { fontSize: 9, fontWeight: 'bold', color: '#1F2937', marginBottom: 3 },
  dpeChipsRow: { flexDirection: 'row', gap: 5 },
  dpeChip: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 3,
  },
  dpeChipLabel: { fontSize: 6, color: '#94A3B8', marginBottom: 1 },
  dpeChipValue: { fontSize: 8.5, color: '#1E293B', fontWeight: 'bold' },
  scenarioNameBadge: {
    backgroundColor: '#EFF6FF',
    borderWidth: 1,
    borderColor: '#BFDBFE',
    borderRadius: 5,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignItems: 'center',
  },
  scenarioNameLabel: { fontSize: 6, color: '#3B82F6', marginBottom: 1 },
  scenarioNameValue: { fontSize: 9, fontWeight: 'bold', color: '#1E40AF' },

  // ── Section principale 2 colonnes ──
  mainRow: { flexDirection: 'row', gap: 8, marginBottom: 8 },

  // ── Colonne gauche : progression DPE ──
  dpeProgressCard: {
    flex: 1,
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 10,
    alignItems: 'center',
  },
  dpeProgressTitle: { fontSize: 8, color: '#6B7280', marginBottom: 8, textAlign: 'center' },
  dpeProgressRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  dpeCol: { alignItems: 'center', gap: 3 },
  dpeColLabel: { fontSize: 6.5, color: '#9CA3AF', marginBottom: 2 },
  dpeLetter: {
    width: 34, height: 34, borderRadius: 6,
    justifyContent: 'center', alignItems: 'center',
  },
  dpeLetterText: { fontSize: 18, fontWeight: 'bold' },
  dpeLetterSubLabel: { fontSize: 6, color: '#6B7280', textAlign: 'center' },
  dpeArrow: { fontSize: 14, color: '#94A3B8' },
  dpeDivider: { height: 1, backgroundColor: '#E5E7EB', width: '100%', marginVertical: 5 },

  // Consommations sous le visuel DPE
  consoRow: { flexDirection: 'row', justifyContent: 'space-between', width: '100%', marginTop: 4 },
  consoLabel: { fontSize: 7.5, color: '#6B7280', flex: 1 },
  consoActuel: { fontSize: 8, color: '#6B7280', textAlign: 'right', marginRight: 4 },
  consoArrow: { fontSize: 8, color: '#94A3B8', marginRight: 4 },
  consoProjet: { fontSize: 8, fontWeight: 'bold', color: '#059669', textAlign: 'right' },

  // ── Colonne droite : métriques ──
  metricsCard: {
    flex: 1.3,
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 10,
  },
  metricsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  metricItem: {
    width: '47%',
    backgroundColor: '#FFFFFF',
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 7,
  },
  metricLabel: { fontSize: 6.5, color: '#6B7280', marginBottom: 3 },
  metricValue: { fontSize: 13, fontWeight: 'bold', color: '#1F2937' },
  metricUnit: { fontSize: 6.5, color: '#6B7280', marginTop: 1 },

  // ── Section travaux ──
  sectionHeading: {
    fontSize: 9, fontWeight: 'bold', color: '#111827',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5,
  },
  workCard: {
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    marginBottom: 5,
    overflow: 'hidden',
  },
  workCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F0F9FF',
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  workCardTitle: { fontSize: 9, fontWeight: 'bold', color: '#0369A1' },
  roiBadge: { paddingHorizontal: 5, paddingVertical: 2, borderRadius: 3, backgroundColor: '#FEF9C3' },
  roiBadgeText: { fontSize: 6.5, fontWeight: 'bold' },
  workCardBody: { padding: 7 },
  workCardAvantApres: { marginBottom: 5 },
  workCardRow: { flexDirection: 'row', alignItems: 'baseline', gap: 3, marginBottom: 2 },
  workCardMiniLabel: { fontSize: 6.5, color: '#9CA3AF', width: 32 },
  workCardAvant: { fontSize: 8, color: '#6B7280', flex: 1 },
  workCardApres: { fontSize: 8, color: '#0369A1', fontWeight: 'bold', flex: 1 },
  workCardMetrics: { flexDirection: 'row', gap: 8, marginTop: 4 },
  workMetric: { flex: 1, alignItems: 'center' },
  workMetricValue: { fontSize: 8, fontWeight: 'bold', color: '#059669' },
  workMetricLabel: { fontSize: 6, color: '#9CA3AF', textAlign: 'center' },
  workMetricCost: { fontSize: 8, fontWeight: 'bold', color: '#D97706' },
  workMetricGES: { fontSize: 8, fontWeight: 'bold', color: '#7C3AED' },

  // ── Tableau synthèse financière ──
  tableContainer: { marginBottom: 8 },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F3F4F6',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 4,
    marginBottom: 1,
  },
  tableRow: {
    flexDirection: 'row',
    paddingHorizontal: 6,
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    alignItems: 'center',
  },
  tableRowTotal: {
    flexDirection: 'row',
    paddingHorizontal: 6,
    paddingVertical: 5,
    backgroundColor: '#F0FDF4',
    borderTopWidth: 1,
    borderTopColor: '#D1FAE5',
    borderRadius: 4,
    alignItems: 'center',
    marginTop: 2,
  },
  tableCellH: { fontSize: 7.5, fontWeight: 'bold', color: '#374151' },
  tableCell: { fontSize: 8, color: '#374151' },
  tableCellGreen: { fontSize: 8, fontWeight: 'bold', color: '#059669' },
  tableCellBold: { fontSize: 8, fontWeight: 'bold', color: '#1F2937' },
  tableCellROI: { fontSize: 8, fontWeight: 'bold' },

  // largeurs colonnes tableau
  colType: { flex: 1.2 },
  colEquip: { flex: 2 },
  colSurface: { flex: 0.8, textAlign: 'right' },
  colCout: { flex: 0.9, textAlign: 'right' },
  colEco: { flex: 1, textAlign: 'right' },
  colEcoEur: { flex: 0.9, textAlign: 'right' },
  colROI: { flex: 0.7, textAlign: 'right' },

  // ── Footer ──
  footer: {
    position: 'absolute', bottom: 14, left: 26, right: 26,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingTop: 5, borderTopWidth: 1, borderTopColor: '#E5E7EB',
  },
  footerWatermark: { fontSize: 7, color: '#D1D5DB' },
  footerNote: { fontSize: 6, color: '#9CA3AF', flex: 1, textAlign: 'center', paddingHorizontal: 8 },
  footerPage: { fontSize: 7, color: '#9CA3AF' },
});

// ─────────────────────────────────────────────
// MAIN DOCUMENT
// ─────────────────────────────────────────────

export const ScenarioPDFDocument: React.FC<ScenarioPDFDocumentProps> = ({
  companyName,
  logoUrl,
  primaryColor,
  scenario,
  dpeData,
}) => {
  const accent = primaryColor ?? '#2563EB';
  const reportDate = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  // ── Champs identité DPE ──
  const address: string = dpeData?.adresse_ban ?? 'Adresse non renseignée';
  const numeroDPE: string = dpeData?.numero_dpe ?? '';
  const dateDPE: string = formatDateFR(dpeData?.date_etablissement_dpe);
  const dateFinValidite: string = formatDateFR(dpeData?.date_fin_validite_dpe);
  const versionDPE: string = dpeData?.version_dpe ?? '';

  // ── Classes DPE ──
  const currentEnergy = scenario.current_energy_class ?? '';
  const projectedEnergy = scenario.projected_energy_class ?? '';
  const currentGES = scenario.current_climate_class ?? '';
  const projectedGES = scenario.projected_climate_class ?? '';

  // ── Consommations ──
  const currentConso = scenario.current_energy_consumption ?? 0;
  const projectedConso = scenario.projected_energy_consumption ?? 0;
  const currentGESVal = scenario.current_ges_emissions ?? 0;
  const projectedGESVal = scenario.projected_ges_emissions ?? 0;

  // ── Métriques financières ──
  const totalEnergySavings = scenario.total_energy_savings ?? 0;
  const totalCostSavings = scenario.total_cost_savings ?? 0;
  const totalInvestment = scenario.total_investment ?? 0;
  const payback = scenario.payback_period_years ?? 0;

  // ── Travaux ──
  const improvements = scenario.improvements ?? [];

  return (
    <Document>
      <Page size="A4" style={s.page}>

        {/* ── HEADER BRANDING ── */}
        <View style={[s.header, { borderBottomColor: accent }]}>
          <View style={s.headerLeft}>
            <Text style={s.companyName}>{companyName || 'DPEWizard'}</Text>
            <Text style={s.headerTitle}>DPE Projeté — Scénario de travaux</Text>
            <Text style={s.headerSubtitle}>Généré le {reportDate}</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {logoUrl ? <Image style={s.logo} src={logoUrl} /> : null}
          </View>
        </View>

        {/* ── BANDEAU IDENTITÉ DPE ── */}
        <View style={s.dpeBandeau}>
          <View style={s.dpeMetaLeft}>
            <Text style={s.dpeAddress}>{address}</Text>
            <View style={s.dpeChipsRow}>
              {numeroDPE ? (
                <View style={s.dpeChip}>
                  <Text style={s.dpeChipLabel}>N° DPE ADEME</Text>
                  <Text style={s.dpeChipValue}>{numeroDPE}</Text>
                </View>
              ) : null}
              {dateDPE ? (
                <View style={s.dpeChip}>
                  <Text style={s.dpeChipLabel}>Établi le</Text>
                  <Text style={s.dpeChipValue}>{dateDPE}</Text>
                </View>
              ) : null}
              {dateFinValidite ? (
                <View style={s.dpeChip}>
                  <Text style={s.dpeChipLabel}>Valide jusqu'au</Text>
                  <Text style={s.dpeChipValue}>{dateFinValidite}</Text>
                </View>
              ) : null}
              {versionDPE ? (
                <View style={s.dpeChip}>
                  <Text style={s.dpeChipLabel}>Version</Text>
                  <Text style={s.dpeChipValue}>DPE v{versionDPE}</Text>
                </View>
              ) : null}
            </View>
          </View>
          {/* Badge nom du scénario */}
          <View style={[s.scenarioNameBadge, { borderColor: accent + '40', backgroundColor: accent + '0D' }]}>
            <Text style={[s.scenarioNameLabel, { color: accent }]}>Scénario</Text>
            <Text style={[s.scenarioNameValue, { color: accent }]}>{truncate(scenario.name, 24)}</Text>
          </View>
        </View>

        {/* ── SECTION PRINCIPALE : progression DPE + métriques ── */}
        <View style={s.mainRow}>

          {/* Colonne gauche : lettres DPE avant/après */}
          <View style={s.dpeProgressCard}>
            <Text style={s.dpeProgressTitle}>
              Classes énergétique et climatique
            </Text>

            {/* Ligne Énergie */}
            <View style={s.dpeProgressRow}>
              <View style={s.dpeCol}>
                <Text style={s.dpeColLabel}>Actuel</Text>
                {currentEnergy ? (
                  <View style={[s.dpeLetter, { backgroundColor: getDPEColor(currentEnergy) }]}>
                    <Text style={[s.dpeLetterText, { color: getDPETextColor(currentEnergy) }]}>
                      {currentEnergy}
                    </Text>
                  </View>
                ) : null}
                <Text style={s.dpeLetterSubLabel}>Énergie</Text>
              </View>
              <Text style={s.dpeArrow}>{'>'}</Text>
              <View style={s.dpeCol}>
                <Text style={s.dpeColLabel}>Projeté</Text>
                {projectedEnergy ? (
                  <View style={[s.dpeLetter, { backgroundColor: getDPEColor(projectedEnergy) }]}>
                    <Text style={[s.dpeLetterText, { color: getDPETextColor(projectedEnergy) }]}>
                      {projectedEnergy}
                    </Text>
                  </View>
                ) : null}
                <Text style={s.dpeLetterSubLabel}>Énergie</Text>
              </View>
            </View>

            <View style={s.dpeDivider} />

            {/* Ligne GES */}
            <View style={s.dpeProgressRow}>
              <View style={s.dpeCol}>
                {currentGES ? (
                  <View style={[s.dpeLetter, { backgroundColor: getDPEColor(currentGES) }]}>
                    <Text style={[s.dpeLetterText, { color: getDPETextColor(currentGES) }]}>
                      {currentGES}
                    </Text>
                  </View>
                ) : null}
                <Text style={s.dpeLetterSubLabel}>GES</Text>
              </View>
              <Text style={s.dpeArrow}>{'>'}</Text>
              <View style={s.dpeCol}>
                {projectedGES ? (
                  <View style={[s.dpeLetter, { backgroundColor: getDPEColor(projectedGES) }]}>
                    <Text style={[s.dpeLetterText, { color: getDPETextColor(projectedGES) }]}>
                      {projectedGES}
                    </Text>
                  </View>
                ) : null}
                <Text style={s.dpeLetterSubLabel}>GES</Text>
              </View>
            </View>

            <View style={s.dpeDivider} />

            {/* Consommations numériques */}
            <View style={[s.consoRow, { marginTop: 2 }]}>
              <Text style={s.consoLabel}>Énergie primaire</Text>
              <Text style={s.consoActuel}>{formatNumber(currentConso, 0)}</Text>
              <Text style={s.consoArrow}>{'>'}</Text>
              <Text style={s.consoProjet}>{formatNumber(projectedConso, 0)} kWh/m²/an</Text>
            </View>
            <View style={s.consoRow}>
              <Text style={s.consoLabel}>Émissions GES</Text>
              <Text style={s.consoActuel}>{formatNumber(currentGESVal, 2)}</Text>
              <Text style={s.consoArrow}>{'>'}</Text>
              <Text style={s.consoProjet}>{formatNumber(projectedGESVal, 2)} kgCO2/m²/an</Text>
            </View>
          </View>

          {/* Colonne droite : 4 métriques clés */}
          <View style={s.metricsCard}>
            <View style={s.metricsGrid}>
              {/* Économies énergie */}
              <View style={s.metricItem}>
                <Text style={s.metricLabel}>Économies annuelles</Text>
                <Text style={[s.metricValue, { color: '#2563EB' }]}>
                  {formatNumber(totalEnergySavings, 0)}
                </Text>
                <Text style={s.metricUnit}>kWh/an</Text>
              </View>

              {/* Économies financières */}
              <View style={s.metricItem}>
                <Text style={s.metricLabel}>Économies financières</Text>
                <Text style={[s.metricValue, { color: '#059669' }]}>
                  {formatCurrency(totalCostSavings)}
                </Text>
                <Text style={s.metricUnit}>par an</Text>
              </View>

              {/* Investissement */}
              <View style={s.metricItem}>
                <Text style={s.metricLabel}>Investissement total</Text>
                <Text style={[s.metricValue, { color: '#D97706' }]}>
                  {formatCurrency(totalInvestment)}
                </Text>
                <Text style={s.metricUnit}>estimé hors aides</Text>
              </View>

              {/* ROI */}
              <View style={s.metricItem}>
                <Text style={s.metricLabel}>Temps de retour</Text>
                <Text style={[s.metricValue, { color: getROIColor(payback) }]}>
                  {formatNumber(payback, 1)}
                </Text>
                <Text style={s.metricUnit}>ans</Text>
              </View>
            </View>
          </View>
        </View>

        {/* ── TRAVAUX PLANIFIÉS ── */}
        {improvements.length > 0 && (
          <View style={{ marginBottom: 7 }}>
            <Text style={s.sectionHeading}>Travaux planifiés</Text>
            {improvements.map((imp, i) => (
              <View key={imp.id ?? i} style={s.workCard}>
                {/* En-tête travail */}
                <View style={[s.workCardHeader, { backgroundColor: accent + '18' }]}>
                  <Text style={[s.workCardTitle, { color: accent }]}>
                    {getImprovementLabel(imp.improvement_type)}
                  </Text>
                  {calculateROI(imp) != null && (
                    <View style={[s.roiBadge, { backgroundColor: getROIColor(calculateROI(imp)!) + '20' }]}>
                      <Text style={[s.roiBadgeText, { color: getROIColor(calculateROI(imp)!) }]}>
                        Retour : {formatNumber(calculateROI(imp)!, 1)} ans
                      </Text>
                    </View>
                  )}
                </View>

                {/* Corps travail */}
                <View style={s.workCardBody}>
                  {/* Avant / Après */}
                  <View style={s.workCardAvantApres}>
                    {imp.before_description ? (
                      <View style={s.workCardRow}>
                        <Text style={s.workCardMiniLabel}>Avant :</Text>
                        <Text style={s.workCardAvant}>{truncate(imp.before_description, 100)}</Text>
                      </View>
                    ) : null}
                    {imp.after_description ? (
                      <View style={s.workCardRow}>
                        <Text style={s.workCardMiniLabel}>Après :</Text>
                        <Text style={s.workCardApres}>{truncate(imp.after_description, 120)}</Text>
                      </View>
                    ) : null}
                  </View>

                  {/* 4 métriques inline */}
                  <View style={s.workCardMetrics}>
                    {imp.energy_savings != null && (
                      <View style={s.workMetric}>
                        <Text style={s.workMetricValue}>
                          {formatNumber(imp.energy_savings, 0)} kWh/an
                        </Text>
                        <Text style={s.workMetricLabel}>Éco. énergie</Text>
                      </View>
                    )}
                    {imp.cost_savings != null && (
                      <View style={s.workMetric}>
                        <Text style={s.workMetricValue}>
                          {formatCurrency(imp.cost_savings)}/an
                        </Text>
                        <Text style={s.workMetricLabel}>Éco. financière</Text>
                      </View>
                    )}
                    {imp.estimated_cost != null && (
                      <View style={s.workMetric}>
                        <Text style={s.workMetricCost}>
                          {formatCurrency(imp.estimated_cost)}
                        </Text>
                        <Text style={s.workMetricLabel}>Coût estimé</Text>
                      </View>
                    )}
                    {imp.ges_reduction != null && (
                      <View style={s.workMetric}>
                        <Text style={s.workMetricGES}>
                          -{formatNumber(imp.ges_reduction, 0)} kgCO2/an
                        </Text>
                        <Text style={s.workMetricLabel}>Réduction GES</Text>
                      </View>
                    )}
                    {imp.affected_surface != null && (
                      <View style={s.workMetric}>
                        <Text style={[s.workMetricValue, { color: '#6B7280' }]}>
                          {formatNumber(imp.affected_surface, 1)} m²
                        </Text>
                        <Text style={s.workMetricLabel}>Surface</Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* ── SYNTHÈSE FINANCIÈRE ── */}
        {improvements.length > 0 && (
          <View style={s.tableContainer}>
            <Text style={s.sectionHeading}>Synthèse financière</Text>
            {/* En-tête tableau */}
            <View style={s.tableHeader}>
              <Text style={[s.tableCellH, s.colType]}>Type</Text>
              <Text style={[s.tableCellH, s.colEquip]}>Équipement</Text>
              <Text style={[s.tableCellH, s.colSurface]}>Surface</Text>
              <Text style={[s.tableCellH, s.colCout]}>Coût</Text>
              <Text style={[s.tableCellH, s.colEco]}>Éco. énergie</Text>
              <Text style={[s.tableCellH, s.colEcoEur]}>Éco. €/an</Text>
              <Text style={[s.tableCellH, s.colROI]}>ROI</Text>
            </View>

            {/* Lignes travaux */}
            {improvements.map((imp, i) => (
              <View key={imp.id ?? i} style={s.tableRow}>
                <Text style={[s.tableCell, s.colType]}>
                  {getImprovementLabel(imp.improvement_type)}
                </Text>
                <Text style={[s.tableCell, s.colEquip]}>
                  {truncate(imp.after_description, 40)}
                </Text>
                <Text style={[s.tableCell, s.colSurface]}>
                  {imp.affected_surface ? formatNumber(imp.affected_surface, 0) + ' m²' : '—'}
                </Text>
                <Text style={[s.tableCellBold, s.colCout]}>
                  {imp.estimated_cost ? formatCurrency(imp.estimated_cost) : '—'}
                </Text>
                <Text style={[s.tableCell, s.colEco]}>
                  {imp.energy_savings ? formatNumber(imp.energy_savings, 0) + ' kWh' : '—'}
                </Text>
                <Text style={[s.tableCellGreen, s.colEcoEur]}>
                  {imp.cost_savings ? formatCurrency(imp.cost_savings) + '/an' : '—'}
                </Text>
                <Text style={[s.tableCellROI, s.colROI,
                  { color: calculateROI(imp) ? getROIColor(calculateROI(imp)!) : '#6B7280' }
                ]}>
                  {calculateROI(imp) != null ? formatNumber(calculateROI(imp)!, 1) : '—'}
                </Text>
              </View>
            ))}

            {/* Ligne TOTAL */}
            <View style={s.tableRowTotal}>
              <Text style={[s.tableCellBold, s.colType]}>TOTAL</Text>
              <Text style={[s.tableCell, s.colEquip]}></Text>
              <Text style={[s.tableCell, s.colSurface]}></Text>
              <Text style={[s.tableCellBold, s.colCout]}>{formatCurrency(totalInvestment)}</Text>
              <Text style={[s.tableCellBold, s.colEco, { color: '#059669' }]}>
                {formatNumber(totalEnergySavings, 0)} kWh
              </Text>
              <Text style={[s.tableCellGreen, s.colEcoEur]}>
                {formatCurrency(totalCostSavings)}/an
              </Text>
              <Text style={[s.tableCellROI, s.colROI, { color: getROIColor(payback) }]}>
                {formatNumber(payback, 1)}
              </Text>
            </View>
          </View>
        )}

        {/* ── FOOTER ── */}
        <View style={s.footer} fixed>
          <Text style={s.footerWatermark}>Généré par DPEWizard</Text>
          <Text style={s.footerNote}>
            Estimations indicatives (moyennes nationales 2025, coeff. électrique 2026 : 1,9) — hors aides financières (MaPrimeRénov', CEE, éco-PTZ). Consultez un professionnel RGE.
          </Text>
          <Text style={s.footerPage} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
        </View>

      </Page>
    </Document>
  );
};

export default ScenarioPDFDocument;